import { useCallback, useState } from 'react';
import { MdOutlineModeEditOutline } from "react-icons/md";
import { useDispatch } from 'react-redux';
import { ActionIcon, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { AppDispatch, RootState } from '../../../redux/store';
import { UsersApiResponse } from '../../../type/entities/user-type';
import { ProviderTypeService } from "../../../services/providerTypes/providerTypeService";
import ProviderTypesFilters from "./ProviderTypesFilters";
import ProviderTypeForm from "../components/ProviderTypeForm";

const columnOrder = [
    'id',
    'name',
    'description',
    'company',
    'createdAt',
    'updatedAt'
];

interface ProviderTypesListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    providerTypeSelected: any;
    setProviderTypeSelected: React.Dispatch<React.SetStateAction<any>>;
}

const ProviderTypesList: React.FC<ProviderTypesListProps> = ({ openFormDrawer, setOpenFormDrawer, providerTypeSelected, setProviderTypeSelected }) => {

    // STATES

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const service = new ProviderTypeService();
    const dispatch = useDispatch<AppDispatch>();

    // HOOKS

    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE TIPOS DE PROVEEDORES
     * @EN GETS THE LIST OF PROVIDER TYPES
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listProviderTypes(filters);
        return response.getResponseData() as UsersApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE UN TIPO DE PROVEEDOR
     * @EN HANDLES THE DELETION OF A PROVIDER TYPE
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteProviderType(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Tipo de proveedor eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE VARIOS TIPOS DE PROVEEDORES
     * @EN HANDLES THE DELETION OF MULTIPLE PROVIDER TYPES
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {

            const response = await (await service.deleteMultiProviderType(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Tipos de proveedor eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'provider-types-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={[]}
                    columnsNotShown={[]}
                    overrideColumns={[
                        {
                            key: 'name',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original.name}
                                    </div>
                                )
                            }
                        },
                        {
                            key: 'description',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original.description ? row.row.original.description : '-'}
                                    </div>
                                )
                            }
                        },
                        {
                            key: 'company',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original.company?.name ? row.row.original.company.name : '-'}
                                    </div>
                                )
                            }
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'provider_type', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar tipo de proveedor'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setProviderTypeSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'provider_type', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar tipo de proveedor'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar tipo de proveedor`}
                                                description={`¿Estás seguro de que deseas eliminar el tipo de proveedor ${row.name}?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as UsersApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Tipos de Proveedor'} onClear={() => resetFilters(50)}>
                <ProviderTypesFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    providerTypeSelected ?
                        "Completa el formulario para editar los datos del tipo de proveedor" :
                        "Completa el formulario para crear un nuevo tipo de proveedor"
                }
                drawerTitle={providerTypeSelected ? 'Editar Tipo de Proveedor' : 'Crear Tipo de Proveedor'} submitText={providerTypeSelected ? 'Guardar Tipo' : 'Crear Tipo'}>
                <ProviderTypeForm setOpenModal={setOpenFormDrawer} providerType={providerTypeSelected} refetch={refetch} />
            </FormDrawerView>
        </Page>
    );
};

export default ProviderTypesList;
